<template>
  <div class="summary-widget" v-if="flat">
    <div class="text-color-space text--small text--medium mb-1">{{ $t('widgets.period.today') }}</div>
    <div class="d-flex align-end">
      <div class="br py-05 px-1 heading-3 text-color-white d-inline-block text--regular mr-1" :style="{ backgroundColor: color, minWidth: '10rem' }" v-if="!$apollo.queries.summary.loading">{{ source === 'USERS' ? computedData.amountToday : toCurrency(computedData.amountToday, computedData.currency) }}</div>
      <div class="br d-inline-block text--regular mr-1" v-else><PuSkeleton height="5rem" width="10rem" /></div>
      <div class="br py-05 px-1 text--small text-color-white d-inline-block text--regular" :class="differenceColor" v-if="difference">
        <span class="icon-triangle d-inline-block" :class="difference < 0 ? 'rotate-180' : null"></span>
        {{ difference }}%
      </div>
    </div>

    <div class="stat-widget__extra d-flex justify-space-between mt-2">
      <div style="width: 50%;">
        <div class="text-color-space text--small text--medium">{{ $t('widgets.SummaryWidget.yesterday') }}</div>
        <div class="heading-5 text--medium" v-if="!$apollo.queries.summary.loading">{{ source === 'USERS' ? computedData.amountPrevDate : toCurrency(computedData.amountPrevDate, merchantCurrency) }}</div>
        <div class="heading-5 text--medium" v-else><PuSkeleton height="2.1rem" width="10rem" /></div>
      </div>

      <div style="width: 50%;">
        <div class="text-color-space text--small text--medium">{{ $t('widgets.SummaryWidget.perMonth') }}</div>
        <div class="heading-5 text--medium" v-if="!$apollo.queries.summary.loading">{{ source === 'USERS' ? computedData.amountMonth : toCurrency(computedData.amountMonth, merchantCurrency) }}</div>
        <div class="heading-5 text--medium" v-else><PuSkeleton height="2.1rem" width="10rem" /></div>
      </div>
    </div>
  </div>

  <box style="max-width: 50rem;" v-else>
    <template v-slot:controls v-if="controls">
      <grab-control @grabstart="(e) => $emit('grabstart', e)" @grabend="(e) => $emit('grabend', e)"></grab-control>
      <kebab-menu>
        <action-link icon-left="icon-settings" size="small" class="mb-1" @click="$emit('configure')" v-if="editable">{{ $t('widgets.Dashboard.configure') }}</action-link>
        <action-link icon-left="icon-trash" size="small" @click="$emit('delete')">{{ $t('widgets.Dashboard.remove') }}</action-link>
      </kebab-menu>
    </template>

    <template v-slot:title>
      <div class="summary-widget-title text-color-space text--bold">{{ title }}</div>
    </template>

    <div class="summary-widget">
<!--      <div class="text-color-space text&#45;&#45;small text&#45;&#45;medium mb-1">{{ $t('widgets.SummaryWidget.total') }}</div>-->
      <div class="text-color-space text--small text--medium mb-1">{{ $t('widgets.period.today') }}</div>
      <div class="d-flex align-end">
        <div class="br py-05 px-1 heading-3 text-color-white d-inline-block text--regular mr-1" :style="`background-color: ${color}`" v-if="!$apollo.queries.summary.loading">{{ source === 'USERS' ? computedData.amountToday : toCurrency(computedData.amountToday, merchantCurrency) }}</div>
        <div class="br d-inline-block text--regular mr-1" v-else><PuSkeleton height="5rem" width="20rem" /></div>
        <div class="br py-05 px-1 text--small text-color-white d-inline-block text--regular" :class="differenceColor" v-if="difference">
          <span class="icon-triangle d-inline-block" :class="difference < 0 ? 'rotate-180' : null"></span>
          {{ difference }}%
        </div>
      </div>

      <div class="stat-widget__extra d-flex justify-space-between mt-2">
        <div style="width: 50%;">
          <div class="text-color-space text--small text--medium">{{ $t('widgets.SummaryWidget.yesterday') }}</div>
          <div class="heading-5 text--medium" v-if="!$apollo.queries.summary.loading">{{ source === 'USERS' ? computedData.amountPrevDate : toCurrency(computedData.amountPrevDate, merchantCurrency) }}</div>
          <div class="heading-5 text--medium" v-else><PuSkeleton height="2.1rem" width="10rem" /></div>
        </div>

        <div style="width: 50%;">
          <div class="text-color-space text--small text--medium">{{ $t('widgets.SummaryWidget.perMonth') }}</div>
          <div class="heading-5 text--medium" v-if="!$apollo.queries.summary.loading">{{ source === 'USERS' ? computedData.amountMonth : toCurrency(computedData.amountMonth, merchantCurrency) }}</div>
          <div class="heading-5 text--medium" v-else><PuSkeleton height="2.1rem" width="10rem" /></div>
        </div>
      </div>
    </div>
  </box>
</template>

<script>
import Box from '@/ui/slots/Box'
import KebabMenu from '@/ui/controls/KebabMenu'
import ActionLink from '@/ui/controls/ActionLink'
import GrabControl from '@/ui/controls/GrabControl'

import currContractId from '@/mixins/currContractId'
import hasActiveContracts from '@/mixins/hasActiveContracts'
import toCurrency from '@/mixins/toCurrency'
import toSimpleDate from '@/mixins/toSimpleDate'
import setClassModifiers from '@/mixins/setClassModifiers'
import { ShowGoldShopStatistics } from '@/mixins/displayRules'

import merchantCurrency from '@/mixins/merchantCurrency'

import { SummaryStatGQL } from '@/graphql/queries'

export default {
  mixins: [currContractId, hasActiveContracts, toCurrency, toSimpleDate, setClassModifiers, merchantCurrency, ShowGoldShopStatistics],

  components: {
    Box,
    KebabMenu,
    ActionLink,
    GrabControl
  },

  props: {
    /**
     * @values #0084F8, #6C78E6, #B9AE15
     */
    color: {
      type: String,
      default: '#0084F8'
    },

    /**
     * @values COMMISSIONS, GOLDSHOP, SALES, USERS
     */
    source: {
      type: String,
      default: 'COMMISSIONS'
    },

    controls: {
      type: Boolean,
      default: false
    },

    editable: {
      type: Boolean,
      default: false
    },

    flat: {
      type: Boolean,
      default: false
    }
  },

  apollo: {
    summary: {
      query: SummaryStatGQL,

      variables () {
        return {
          params: {
            // contractId: this.currContractId,
            stat: this.source
          }
        }
      },

      update: data => data.merchant.stats.summary,

      skip () { return !this.hasActiveContracts || (!this.ShowGoldShopStatistics && this.source === 'GOLDSHOP') }
    }
  },

  computed: {
    title () {
      const titles = {
        COMMISSIONS: this.$i18n.t('widgets.SummaryWidget.storesTotalComission'),
        GOLDSHOP: this.$i18n.t('widgets.SummaryWidget.earnedByGoldShop'),
        SALES: this.$i18n.t('widgets.SummaryWidget.totalSales'),
        USERS: this.$i18n.t('widgets.SummaryWidget.registeredReferrals')
      }

      return titles[this.source]
    },

    computedData () {
      if (!this.summary) {
        return {
          totalAmount: '—',
          amountMonth: '—',
          amountPrevDate: '—',
          amountPrevMonth: '—',
          amountToday: '—'
        }
      }
      return this.summary
    },

    difference () {
      if (!this.summary) {
        return 0
      }

      let amountPrevDate = this.summary.amountPrevDate
      let amountToday = this.summary.amountToday
      if (amountToday === null) amountToday = 0
      if (amountPrevDate === null) amountPrevDate = 0
      //
      return amountPrevDate > 0 ? Math.round(((amountToday - amountPrevDate) / amountPrevDate) * 100) : 0
    },

    differenceColor () {
      if (this.difference > 0) {
        return 'bg-green'
      }

      if (this.difference < 0) {
        return 'bg-error'
      }

      return 'bg-grey-dark'
    }
  }
}
</script>
